import { decodeJwtPayload, getActiveAccount, getToken } from "./AuthorizationUtils";
import { AppRoles } from "./AuthorizationConfig";
import { msalInit } from "./AuthorizationProvider";

interface TokenClaims {
    name?: string;
    preferred_username?: string;
    email?: string;
    upn?: string;
    roles?: string[];
}

export class UserInformation {
    constructor(
        public readonly name: string,
        public readonly email: string,
        public readonly role: string
    ) {}

    static async fromTokenClaims(): Promise<UserInformation | null> {
        const claims = await getTokenClaims();
        if (!claims) return null;

        const name = claims.name || claims.preferred_username || 'Unknown User';      
        const email = claims.email || claims.upn || claims.preferred_username || 'Unknown Email';
        
        // More flexible role extraction
        const roles = claims.roles;
        var role = null;

        // Add prioritization logic for roles
        if (roles) {
            if (roles.includes(AppRoles.ADMIN)) {
                role = AppRoles.ADMIN;
            } 
            else if (roles.includes(AppRoles.CUSTOMER)) {
                role = AppRoles.CUSTOMER;
            } else {
                role = roles[0]; // Fallback to the first role
            }
        }

        if (!role) {
            console.warn("No valid role found in token claims");
            return null;
        }

        return new UserInformation(
            String(name),
            String(email),
            String(role)
        );
    }
}


const getTokenClaims = async (): Promise<TokenClaims | null> => {
    try {
        await msalInit;

        const account = await getActiveAccount();
        if (!account) {
            throw new Error("No active account found");
        }

        if (!account.idTokenClaims) {
            throw new Error("No ID token claims present in account");
        }

        const idClaims = account.idTokenClaims as TokenClaims;
        let roles = idClaims.roles;
        // App roles may be updated in the access token before idTokenClaims cache updates (e.g. after group add + forceRefresh).
        if (!roles?.length) {
            const at = await getToken();
            const payload = at ? decodeJwtPayload(at) : null;
            const atRoles = payload?.roles;
            if (Array.isArray(atRoles) && atRoles.length > 0) {
                roles = atRoles as string[];
            }
        }

        const claims: TokenClaims = { ...idClaims, roles };

        return claims;
    } catch (error) {
        console.error("Error getting token claims:", error);
        throw error;
    }
};

export const getUserName = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.name ?? null;
};

export const getEmail = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.email ?? null;
};

export const getRole = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.role ?? null;
};

// Direct role checking functions
export const checkIsAdmin = async (): Promise<boolean> => {
    const role = await getRole();
    return role === AppRoles.ADMIN;
};

export const checkIsCustomer = async (): Promise<boolean> => {
    const role = await getRole();
    return role === AppRoles.CUSTOMER;
};